import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { registrationSchema } from '../../utils/validation/registrationValidation';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { User, Mail, Lock, Calendar, Clock, MapPin } from 'lucide-react';
import type { RegistrationData } from '../../types/auth';

export const RegistrationForm: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { register } = useAuth();

  const { register: registerField, handleSubmit, formState: { errors } } = useForm<RegistrationData>({
    resolver: zodResolver(registrationSchema)
  });

  const onSubmit = async (data: RegistrationData) => {
    setLoading(true);
    try {
      await register(data.email, data.password, {
        fullName: data.name,
        birthDate: data.birth_date,
        birthTime: data.birth_time,
        birthPlace: data.birth_place,
      });
      toast.success('Cadastro realizado com sucesso!');
      navigate('/');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Erro ao criar conta');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-purple-200 mb-1">
          <User className="w-4 h-4 inline mr-2" />
          Nome Completo
        </label>
        <input
          {...registerField('name')}
          className="input-primary"
          placeholder="Seu nome completo"
          disabled={loading}
        />
        {errors.name && (
          <p className="text-red-400 text-sm mt-1">{errors.name.message}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-purple-200 mb-1">
          <Mail className="w-4 h-4 inline mr-2" />
          Email
        </label>
        <input
          type="email"
          {...registerField('email')}
          className="input-primary"
          placeholder="seu@email.com"
          disabled={loading}
        />
        {errors.email && (
          <p className="text-red-400 text-sm mt-1">{errors.email.message}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-purple-200 mb-1">
          <Lock className="w-4 h-4 inline mr-2" />
          Senha
        </label>
        <input
          type="password"
          {...registerField('password')}
          className="input-primary"
          placeholder="********"
          disabled={loading}
        />
        {errors.password && (
          <p className="text-red-400 text-sm mt-1">{errors.password.message}</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-purple-200 mb-1">
          <Calendar className="w-4 h-4 inline mr-2" />
          Data de Nascimento
        </label>
        <input
          type="date"
          {...registerField('birth_date')}
          className="input-primary"
          disabled={loading}
        />
        {errors.birth_date && (
          <p className="text-red-400 text-sm mt-1">{errors.birth_date.message}</p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-purple-200 mb-1">
            <Clock className="w-4 h-4 inline mr-2" />
            Horário de Nascimento
          </label>
          <input
            type="time"
            {...registerField('birth_time')}
            className="input-primary"
            disabled={loading}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-purple-200 mb-1">
            <MapPin className="w-4 h-4 inline mr-2" />
            Local de Nascimento
          </label>
          <input
            {...registerField('birth_place')}
            className="input-primary"
            placeholder="Cidade/Estado"
            disabled={loading}
          />
        </div>
      </div>

      <button
        type="submit"
        disabled={loading}
        className="btn-primary w-full"
      >
        {loading ? (
          <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
        ) : (
          'Criar Conta'
        )}
      </button>
    </form>
  );
};